import axios from 'axios';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

import { GoogleLoginSuccessCallback } from '../../../components/google-login/useGoogleLoginIFrameEventListener';
import { buildSignUpHref } from '../../_shared/sign-up/buildSignUpHref';
import { LoginErrorType, useLoginForm } from './LoginFormContext';
import { GoogleLoginBody, LoginResponse } from './LoginSchemas';

export function useGoogleSignInCallback(redirectAfterLogin?: string): GoogleLoginSuccessCallback {
    const router = useRouter();
    const { setLoginErrorType, externalAuthId } = useLoginForm();

    return useCallback<GoogleLoginSuccessCallback>(
        (response) => {
            // Google one-tap already carries Google's own risk signal, so Google login sends no
            // reCAPTCHA token.
            axios
                .post<LoginResponse>('/api/login', {
                    googleCredential: response.credential,
                    agreementCheckbox: true,
                    redirectAfterLogin,
                    externalAuthId,
                } satisfies GoogleLoginBody)
                .then((result) => {
                    if (result.data.success) {
                        const redirectTo = result.data.redirectTo ?? '/';
                        if (redirectTo.startsWith('http')) {
                            window.location.href = redirectTo;
                        } else {
                            router.push(redirectTo);
                        }
                    } else if (result.data.reason === 'googleUserNotFound') {
                        router.push(buildSignUpHref({ redirectAfterRegister: redirectAfterLogin, externalAuthId }));
                    } else {
                        // Google login is never rate-limited (lockout is credential-only), so the
                        // server cannot answer 'accountLocked' here.
                        setLoginErrorType(LoginErrorType.INVALID_CREDENTIALS);
                    }
                })
                .catch(() => setLoginErrorType(LoginErrorType.INVALID_CREDENTIALS));
        },
        [router, setLoginErrorType, redirectAfterLogin, externalAuthId],
    );
}
